import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { UserPlus, AlertCircle, FileText, CheckCircle2, Upload } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = {
  primary: '#1E3A5F',
  primaryHover: '#152D4A',
  accent: '#3B82F6',
  lightBg: '#EFF6FF',
};

const SignupPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const partnerCode = searchParams.get('pc') || '';
  const subPartnerCode = searchParams.get('subpc') || '';

  const [partnerBranding, setPartnerBranding] = useState(null);
  const [loadingBranding, setLoadingBranding] = useState(true);
  const [error, setError] = useState(null);

  const [clientName, setClientName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [cmrFile, setCmrFile] = useState(null);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);

  useEffect(() => {
    if (!partnerCode) {
      setError('Missing partner code. Please use a valid signup link.');
      setLoadingBranding(false);
      return;
    }
    const fetchBranding = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/partner-branding/${partnerCode}`);
        setPartnerBranding(res.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Invalid partner code. Please check your signup link.');
        } else {
          setError('Failed to load partner information. Please try again.');
        }
      } finally {
        setLoadingBranding(false);
      }
    };
    fetchBranding();
  }, [partnerCode]);

  // Set partner favicon and title if available
  useEffect(() => {
    if (partnerBranding?.partner_favicon_url) {
      const fav = partnerBranding.partner_favicon_url;
      const name = partnerBranding.partner_name || 'BondMaster';
      document.title = `${name} | Client Registration`;
      const setLink = (rel, href) => {
        let el = document.querySelector(`link[rel="${rel}"]`);
        if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); document.head.appendChild(el); }
        el.setAttribute('href', href);
      };
      setLink('icon', fav);
      setLink('shortcut icon', fav);
      setLink('apple-touch-icon', fav);
    }
    return () => {
      document.title = 'BondMaster | Bond Portfolio Management Platform';
      const def = 'https://bondmaster.com.in/logoBond.png';
      ['icon', 'shortcut icon', 'apple-touch-icon'].forEach(rel => {
        const el = document.querySelector(`link[rel="${rel}"]`);
        if (el) el.setAttribute('href', def);
      });
    };
  }, [partnerBranding]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error('Only PDF files are accepted for CMR');
        e.target.value = '';
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be under 10 MB');
        e.target.value = '';
        return;
      }
      setCmrFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!clientName.trim()) { toast.error('Please enter your full name'); return; }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error('Please enter a valid email address'); return; }
    if (!mobile.trim() || !/^\d{10}$/.test(mobile)) { toast.error('Mobile number must be 10 digits'); return; }
    if (!cmrFile) { toast.error('Please upload your CMR document (PDF)'); return; }
    if (!tosAccepted) { toast.error('Please accept the Terms of Service'); return; }

    setIsSubmitting(true);
    try {
      // Step 1: Create client
      const payload = {
        partner_code: partnerCode,
        client_name: clientName.trim(),
        email: email.trim(),
        mobile: mobile.trim(),
      };
      if (subPartnerCode) {
        payload.subpartner_code = subPartnerCode;
      }
      const signupRes = await axios.post(`${API_URL}/api/client-signup`, payload);

      const clientId = signupRes.data.client_id;

      // Step 2: Upload CMR PDF
      const formData = new FormData();
      formData.append('file', cmrFile);
      await axios.post(`${API_URL}/api/client-signup-cmr/${clientId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSignupSuccess(true);
      toast.success('Registration successful!');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (loadingBranding) {
    return (
      <div className="login-container" data-testid="signup-page-loading" style={{ background: `linear-gradient(135deg, ${COLORS.lightBg} 0%, #DBEAFE 100%)` }}>
        <div className="w-full max-w-md px-4 text-center">
          <div className="animate-spin h-8 w-8 border-4 border-[#1E3A5F]/30 border-t-[#1E3A5F] rounded-full mx-auto"></div>
          <p className="text-[#64748b] mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="login-container" data-testid="signup-page-error" style={{ background: `linear-gradient(135deg, ${COLORS.lightBg} 0%, #DBEAFE 100%)` }}>
        <div className="w-full max-w-md px-4">
          <Card className="glass-card border-0 shadow-xl">
            <CardContent className="pt-8 pb-8 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-heading font-bold text-[#1E3A5F] mb-2">Error</h2>
              <p className="text-[#64748b] mb-6">{error}</p>
              <Button onClick={() => navigate('/')} className="bg-[#1E3A5F] hover:bg-[#152D4A] text-white">
                Go to Main Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Success state
  if (signupSuccess) {
    return (
      <div className="login-container" data-testid="signup-page-success" style={{ background: `linear-gradient(135deg, ${COLORS.lightBg} 0%, #DBEAFE 100%)` }}>
        <div className="w-full max-w-md px-4">
          <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm">
            <CardContent className="pt-10 pb-10 text-center">
              <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-heading font-bold text-[#1E3A5F] mb-2">Registration Complete!</h2>
              <p className="text-[#64748b] mb-6">
                Your account has been created. Your partner will provide you with login credentials shortly.
              </p>
              <Button onClick={() => navigate('/')} className="bg-[#1E3A5F] hover:bg-[#152D4A] text-white" data-testid="signup-success-login-btn">
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const hasCustomLogo = partnerBranding?.partner_logo_url && partnerBranding.partner_logo_url.trim() !== '';
  const logoUrl = hasCustomLogo ? partnerBranding.partner_logo_url : 'https://bondmaster.com.in/logoBond.png';

  return (
    <div className="login-container" data-testid="signup-page" style={{ background: `linear-gradient(135deg, ${COLORS.lightBg} 0%, #DBEAFE 100%)` }}>
      <div className="w-full max-w-md px-4 py-8">
        {/* Logo and Header */}
        <div className="text-center mb-6 animate-fadeIn">
          <img
            src={logoUrl}
            alt="Logo"
            className="h-16 mx-auto mb-3 max-w-[200px] object-contain"
            data-testid="signup-logo"
          />
          <p className="text-[#475569] text-sm">Create your investment account</p>
        </div>

        {/* Signup Card */}
        <Card className="border-0 shadow-xl animate-fadeIn bg-white/80 backdrop-blur-sm" style={{ animationDelay: '0.1s' }}>
          <CardHeader className="space-y-1 pb-3">
            <CardTitle className="text-2xl font-heading text-center text-[#1E3A5F]">
              Client Registration
            </CardTitle>
            <CardDescription className="text-center text-[#64748b]">
              Fill in your details to get started
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Full Name */}
              <div className="space-y-1.5">
                <Label htmlFor="clientName" className="text-[#1E3A5F] font-medium text-sm">Full Name *</Label>
                <Input
                  id="clientName"
                  type="text"
                  placeholder="Enter your full name"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  className="h-10 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-[#3B82F6]/20 focus:border-[#3B82F6]"
                  data-testid="signup-name-input"
                />
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-[#1E3A5F] font-medium text-sm">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-10 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-[#3B82F6]/20 focus:border-[#3B82F6]"
                  data-testid="signup-email-input"
                />
              </div>

              {/* Mobile */}
              <div className="space-y-1.5">
                <Label htmlFor="mobile" className="text-[#1E3A5F] font-medium text-sm">Mobile Number *</Label>
                <Input
                  id="mobile"
                  type="tel"
                  placeholder="10-digit mobile number"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  className="h-10 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-[#3B82F6]/20 focus:border-[#3B82F6]"
                  data-testid="signup-mobile-input"
                />
              </div>

              {/* CMR Upload */}
              <div className="space-y-1.5">
                <Label htmlFor="cmrUpload" className="text-[#1E3A5F] font-medium text-sm">CMR Document (PDF) *</Label>
                <div className="relative">
                  <Input
                    id="cmrUpload"
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="h-10 bg-slate-50 border-slate-200 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-[#1E3A5F] file:text-white hover:file:bg-[#152D4A] cursor-pointer"
                    data-testid="signup-cmr-input"
                  />
                </div>
                {cmrFile && (
                  <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                    <FileText className="h-3 w-3" /> {cmrFile.name}
                  </p>
                )}
              </div>

              {/* Terms of Service */}
              <div className="flex items-start space-x-2 pt-1">
                <Checkbox
                  id="tos"
                  checked={tosAccepted}
                  onCheckedChange={(checked) => setTosAccepted(checked === true)}
                  className="mt-0.5 data-[state=checked]:bg-[#1E3A5F] data-[state=checked]:border-[#1E3A5F]"
                  data-testid="signup-tos-checkbox"
                />
                <Label htmlFor="tos" className="text-sm text-[#475569] leading-snug cursor-pointer">
                  I have read and agree to the{' '}
                  <Link to="/tnc" target="_blank" className="text-[#3B82F6] underline hover:text-[#1E3A5F]" data-testid="signup-tos-link">
                    Terms of Service
                  </Link>
                </Label>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                className="w-full h-11 bg-[#1E3A5F] hover:bg-[#152D4A] text-white font-medium rounded-lg transition-all duration-200 mt-2"
                disabled={isSubmitting}
                data-testid="signup-submit-btn"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full"></span>
                    Registering...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <UserPlus className="h-4 w-4" />
                    Register
                  </span>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-[#64748b] mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-[#3B82F6] underline hover:text-[#1E3A5F]" data-testid="signup-login-link">
            Sign In
          </Link>
        </p>
      </div>
    </div>
  );
};

export default SignupPage;
